import { useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { invokeApi, API_URL } from '@/lib/apiClient';
import type { TestQuestion, TestResult } from '@/types/oposiciones';
import {
  STORAGE_KEY,
  MAX_KEEP,
  TTL_MS,
  cuerpoToExamType,
  cuerpoToBaseRoute,
  type PendingGen,
} from './pendingGenerations.constants';
import { getDownloadModeFromCuerpo } from '@/services/downloadHistoryService';
import {
  PendingGenContext,
  type PendingGenContextValue,
} from './pending-generations.context';

// Note: hooks and types are exported from './pending-generations.context'
// to keep this file "component-only" and friendly to React Fast Refresh.



function loadFromStorage(): PendingGen[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PendingGen[];
    const now = Date.now();
    return parsed.filter(g => now - g.createdAt < TTL_MS).slice(-MAX_KEEP);
  } catch {
    return [];
  }
}

function saveToStorage(list: PendingGen[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(-MAX_KEEP)));
  } catch {
    /* ignore quota */
  }
}

function uid(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Ejecuta `fn` con un reintento automático si falla. Usa backoff lineal
 * para no saturar el backend si está en mantenimiento momentáneo.
 */
async function withRetry<T>(fn: () => Promise<T>, retries = 1, delayMs = 3000): Promise<T> {
  let lastErr: any;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (err: any) {
      lastErr = err;
      // Si es un error de validación / cuota, no reintentar
      const msg = String(err?.message || '').toLowerCase();
      if (msg.includes('cuota') || msg.includes('quota') || msg.includes('límite') || msg.includes('limit reached')) {
        throw err;
      }
      if (attempt < retries) {
        console.warn(`[withRetry] intento ${attempt + 1} falló, reintentando en ${delayMs}ms…`, err?.message);
        await new Promise(r => setTimeout(r, delayMs));
      }
    }
  }
  throw lastErr;
}

export function PendingGenerationsProvider({ children }: { children: ReactNode }) {
  const [generations, setGenerations] = useState<PendingGen[]>(() => loadFromStorage());
  const genRef = useRef<PendingGen[]>(generations);
  
  useEffect(() => {
    genRef.current = generations;
    saveToStorage(generations);
  }, [generations]);

  useEffect(() => {
    const now = Date.now();
    setGenerations(prev =>
      prev.map(g =>
        g.status === 'generating' && now - g.createdAt > 1000 * 60 * 10 // Increase to 10 mins for NotebookLM
          ? { ...g, status: 'error', errorMessage: 'Tiempo de espera agotado' }
          : g,
      ),
    );
  }, []);

  const start = useCallback<PendingGenContextValue['start']>(({ type, cuerpo, topic, id: providedId }) => {
    const id = providedId || uid();
    // For slides/infographic/report, they usually go to the same base route or a specific downloads area
    // but the user wants them in the dashboard/test pages.
    const route = `${cuerpoToBaseRoute[cuerpo]}/${type === 'test' ? 'test' : type === 'case' ? 'caso' : 'recursos'}`;
    
    const entry: PendingGen = {
      id,
      type,
      cuerpo,
      topic,
      status: 'generating',
      route,
      createdAt: Date.now(),
    };
    setGenerations(prev => [...prev, entry]);
    return id;
  }, []);

  const markReady = useCallback<PendingGenContextValue['markReady']>((id, resultId, fullRoute) => {
    setGenerations(prev =>
      prev.map(g =>
        g.id === id
          ? { ...g, status: 'ready', resultId, fullRoute: fullRoute || `${g.route}?view=${resultId}` }
          : g,
      ),
    );
  }, []);

  const markError = useCallback<PendingGenContextValue['markError']>((id, message) => {
    setGenerations(prev =>
      prev.map(g => (g.id === id ? { ...g, status: 'error', errorMessage: message } : g)),
    );
  }, []);

  const markSeen = useCallback<PendingGenContextValue['markSeen']>(id => {
    setGenerations(prev => prev.map(g => (g.id === id ? { ...g, status: 'seen' } : g)));
  }, []);

  const clear = useCallback<PendingGenContextValue['clear']>(id => {
    setGenerations(prev => prev.filter(g => g.id !== id));
  }, []);

  const registerReady = useCallback<PendingGenContextValue['registerReady']>(({ type, cuerpo, topic, resultId }) => {
    const id = uid();
    const route = `${cuerpoToBaseRoute[cuerpo]}/${type === 'test' ? 'test' : 'caso'}`;
    const entry: PendingGen = {
      id,
      type,
      cuerpo,
      topic,
      status: 'ready',
      resultId,
      route,
      fullRoute: `${route}?view=${resultId}`,
      createdAt: Date.now(),
    };
    setGenerations(prev => [...prev, entry]);
    return id;
  }, []);

  const runBackgroundTest = useCallback<PendingGenContextValue['runBackgroundTest']>(
    ({ cuerpo, userId, testConfig, topicName }) => {
      const examType = cuerpoToExamType[cuerpo];
      const id = start({ type: 'test', cuerpo, topic: topicName || testConfig.topic });

      (async () => {
        try {
          const { saveTestResult } = await import('@/services/oposicionesService');
          const data = await withRetry(async () => {
            const { data, error } = await invokeApi('generate-test', {
              body: {
                topic: testConfig.topic,
                topicNumber: testConfig.topicNumber,
                topicId: testConfig.topicId,
                questionCount: testConfig.questionCount,
                difficulty: testConfig.difficulty,
                examType: examType === 'secretaria' ? 'sial' : examType,
              },
            });
            if (error) throw error;
            return data;
          });
          const testData = data as { questions?: TestQuestion[] };
          if (!testData.questions || testData.questions.length === 0) {
            throw new Error('No se pudieron generar las preguntas');
          }

          const result: Omit<TestResult, 'testId'> = {
            topic: testConfig.topic,
            score: 0,
            correctCount: 0,
            totalQuestions: testData.questions.length,
            durationSeconds: 0,
            timeLimitSeconds: testConfig.timeLimitMinutes ? testConfig.timeLimitMinutes * 60 : undefined,
            questions: testData.questions,
            answers: new Array(testData.questions.length).fill(null),
            completedAt: new Date(),
          };
          const savedId = await saveTestResult(userId, result, examType);
          markReady(id, savedId);
        } catch (err: any) {
          console.error('[runBackgroundTest] failed', err);
          markError(id, err?.message || 'Error al generar el test');
        }
      })();

      return id;
    },
    [start, markReady, markError],
  );

  const runBackgroundCase = useCallback<PendingGenContextValue['runBackgroundCase']>(
    ({ cuerpo, userId, topicId, topicLabel, difficulty, caseMode }) => {
      // Para Secretaría usamos siempre 'secretaria' como examType para
      // mantener coherencia con el stream local (useCaseStream) y compartir caché.
      const examType = cuerpo === 'secretaria' ? 'secretaria' : cuerpoToExamType[cuerpo];
      const topic = topicLabel || topicId;
      const id = start({ type: 'case', cuerpo, topic });

      (async () => {
        try {
          const [{ saveCaseResult }, { saveDownload }] = await Promise.all([
            import('@/services/oposicionesService'),
            import('@/services/downloadHistoryService'),
          ]);
          const { getApiHeaders } = await import('@/lib/apiClient');
          // API_URL is now imported from @/lib/apiClient at the top level

          const caseData = await withRetry(async () => {
            const headers = await getApiHeaders();
            const response = await fetch(`${API_URL}/evaluate-case`, {
              method: 'POST',
              headers: { ...headers, 'Content-Type': 'application/json' },
              body: JSON.stringify({
                action: 'generate',
                examType,
                topic,
                topicId,
                topicLabel: topic,
                difficulty: difficulty || 'media',
                caseMode: caseMode || 'dual',
              }),
            });
            if (!response.ok) {
              throw new Error(`Error generando caso (${response.status})`);
            }
            const data = await response.json();
            const narrative = data.caseText || data.scenario ||
              (Array.isArray(data.juridicalCase?.antecedentes)
                ? data.juridicalCase.antecedentes.join('\n\n')
                : '');
            if (!narrative && !data.juridicalCase && !data.economicCase) {
              throw new Error('No se pudo generar el caso');
            }
            return {
              ...data,
              narrative,
              questions: Array.isArray(data.questions) ? data.questions : [],
              caseText: narrative,
            };
          });

          if (!caseData || !caseData.narrative) {
            throw new Error('No se pudo generar el caso');
          }

          // Persist a placeholder so the user can retrieve it later. The real
          // user_answer/ai_feedback get filled when the user actually completes
          // the case from the UI.
          const caseText = caseData.caseText || caseData.narrative;

          const savedRow: any = await saveCaseResult(
            userId,
            {
              title: `Caso · ${topic}`,
              topic,
              caseText,
              userAnswer: '',
              aiFeedback: null as any,
              modelAnswer: '',
              completedAt: undefined as any,
            } as any,
            examType,
          );
          const savedId: string = savedRow?.id ?? id;

          // Cache the raw case payload in sessionStorage so the page can
          // re-hydrate it without re-generating.
          try {
            sessionStorage.setItem(`munilex.case.${savedId}`, JSON.stringify(caseData));
          } catch { /* ignore quota */ }

          const baseRoute = cuerpoToBaseRoute[cuerpo];
          if (userId) {
            await saveDownload({
              userId,
              title: `Caso: ${topic}`,
              downloadType: 'case_pdf',
              mode: getDownloadModeFromCuerpo(cuerpo),
              metadata: {
                topic,
                generated_only: true,
                view_url: `${baseRoute}/caso?view=${savedId}`,
              },
            });
          }
          markReady(id, savedId, `${baseRoute}/caso?view=${savedId}`);
        } catch (err: any) {
          console.error('[runBackgroundCase] failed', err);
          markError(id, err?.message || 'Error al generar el caso práctico');
        }
      })();

      return id;
    },
    [start, markReady, markError],
  );

  const runBackgroundFlashcards = useCallback<PendingGenContextValue['runBackgroundFlashcards']>(
    ({ cuerpo, userId, topic, topicLabel, count }) => {
      const examType = cuerpoToExamType[cuerpo];
      const displayTopic = topicLabel || topic;
      const id = start({ type: 'flashcard', cuerpo, topic: displayTopic });

      (async () => {
        try {
          const { createFlashcard } = await import('@/services/oposicionesService');
          const data = await withRetry(async () => {
            const { data, error } = await invokeApi('generate-flashcards', {
              body: { topic, count, examType },
            });
            if (error) throw error;
            return data;
          });
          const fc = (data as any)?.flashcards;
          if (!Array.isArray(fc) || fc.length === 0) {
            throw new Error('No se generaron flashcards');
          }

          let saved = 0;
          for (const card of fc) {
            try {
              await createFlashcard(userId, card.front, card.back, displayTopic, examType);
              saved++;
            } catch (e) {
              console.error('[runBackgroundFlashcards] save failed', e);
            }
          }
          if (saved === 0) throw new Error('No se pudo guardar ninguna flashcard');

          const baseRoute = cuerpoToBaseRoute[cuerpo];
          markReady(id, id, `${baseRoute}/flashcards`);
        } catch (err: any) {
          console.error('[runBackgroundFlashcards] failed', err);
          markError(id, err?.message || 'Error al generar las flashcards');
        }
      })();

      return id;
    },
    [start, markReady, markError],
  );
  const runBackgroundGamma = useCallback<PendingGenContextValue['runBackgroundGamma']>(
    ({ topic, topicId, format, cuerpo, userId, mode, content }) => {
      const id = start({ type: format === 'infografia' ? 'infographic' : 'presentation', cuerpo, topic });

      (async () => {
        try {
          const [{ generateGammaDocument }, { saveDownload }, { getBaseComunPdfUrl }] = await Promise.all([
            import('@/services/gammaService'),
            import('@/services/downloadHistoryService'),
            import('@/data/baseComunPdfResources'),
          ]);

          // Si el tema tiene un PDF oficial pregenerado de presentación (Base Común), usarlo directamente
          const localPdf = getBaseComunPdfUrl(topicId || topic);
          if (localPdf) {
            if (userId) {
              const dlType = format === 'infografia' ? 'infografia' : 'slides';
              await saveDownload({
                userId,
                title: topic,
                downloadType: dlType,
                mode: (mode && mode !== 'oposiciones' ? mode : getDownloadModeFromCuerpo(cuerpo)) as any,
                fileUrl: localPdf,
                metadata: { export_url: localPdf, local_official_pdf: true }
              });
            }
            markReady(id, id, localPdf);
            return;
          }

          const result = await generateGammaDocument({
            topicId,
            topicQuery: topic,
            content,
            format,
            examType: cuerpoToExamType[cuerpo] || 'oposiciones',
          });

          let fullRoute = '';
          if (result.exportUrl) {
            fullRoute = result.exportUrl;
          } else if (result.gammaUrl) {
            fullRoute = result.gammaUrl;
          } else if (result.deck || (result as any).isMunilexDeck) {
            const deckId = (result.deck && result.deck.id) || `deck-${Date.now()}`;
            try {
              sessionStorage.setItem(`munilex_deck_${deckId}`, JSON.stringify(result.deck));
            } catch (e) {
              console.warn('[PendingGenerations] Error saving deck to sessionStorage:', e);
            }
            fullRoute = `/app/presentacion-demo?deckId=${deckId}`;
          } else if (result.markdown) {
            // Local fallback: compilar el markdown directamente a un Blob PDF real (application/pdf)
            const { markdownToBlobUrl } = await import('@/lib/openGenerationResult');
            fullRoute = await markdownToBlobUrl(result.markdown, topic);
          }

          if (userId) {
            const dlType = format === 'infografia' ? 'infografia' : 'slides';
            await saveDownload({
              userId,
              title: topic,
              downloadType: dlType,
              mode: (mode && mode !== 'oposiciones' ? mode : getDownloadModeFromCuerpo(cuerpo)) as any,
              fileUrl: result.exportUrl || (fullRoute.startsWith('blob:') ? undefined : fullRoute),
              metadata: { 
                export_url: result.exportUrl, 
                gamma_url: result.gammaUrl, 
                has_markdown: !!result.markdown,
                content: result.markdown, 
                enriched_blueprint: !!content 
              }
            });
          }

          if (!fullRoute) {
            throw new Error('La generación se completó pero no devolvió ningún contenido visible.');
          }
          markReady(id, id, fullRoute);
        } catch (err: any) {
          console.error('[runBackgroundGamma] failed', err);
          markError(id, err?.message || 'Error al generar material en Gamma');
        }
      })();

      return id;
    },
    [start, markReady, markError]
  );

  const runBackgroundResumen = useCallback<PendingGenContextValue['runBackgroundResumen']>(
    ({ topic, cuerpo, userId, mode, exportType = 'word', content }) => {
      const id = start({ type: 'report', cuerpo, topic });

      (async () => {
        try {
          const [{ generateResumenStream }, { saveDownload }, exportService] = await Promise.all([
            import('@/services/resumenService'),
            import('@/services/downloadHistoryService'),
            import('@/services/exportService'),
          ]);
          let fullMarkdown = '';
          let finalTitle = '';
          
          const examType = cuerpoToExamType[cuerpo] || 'sial';
          const stream = generateResumenStream({ 
            topicQuery: topic, 
            content,
            autonomousCommunity: 'general',
            examType
          });

          for await (const chunk of stream) {
            if (chunk.chunk) fullMarkdown += chunk.chunk;
            if (chunk.title) finalTitle = chunk.title;
          }

          if (!fullMarkdown || fullMarkdown.length < 500) {
            throw new Error('Contenido insuficiente generado');
          }

          const filename = topic.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50);
          let fileUrl = '';
          
          // Note: In a real background scenario, exporting to local disk might be tricky if the user closed the page.
          // But for now, we'll try to trigger it. If it fails, at least the record is saved.
          if (exportType === 'word') {
            await exportService.exportToWord(fullMarkdown, filename, mode as any);
          } else {
            await exportService.exportToPDF(fullMarkdown, `${filename}.pdf`, mode as any);
          }

          if (userId) {
            await saveDownload({ 
              userId, 
              title: finalTitle || topic, 
              downloadType: 'resumen', 
              mode: (mode && mode !== 'oposiciones' ? mode : getDownloadModeFromCuerpo(cuerpo)) as any, 
              metadata: { content: fullMarkdown } 
            });
          }

          markReady(id, id, ''); // No specific URL for local downloads, maybe we should save it to Supabase storage?
        } catch (err: any) {
          console.error('[runBackgroundResumen] failed', err);
          markError(id, err?.message || 'Error al generar resumen');
        }
      })();

      return id;
    },
    [start, markReady, markError]
  );

  const value = useMemo<PendingGenContextValue>(
    () => ({ 
      generations, start, markReady, markError, markSeen, clear, 
      runBackgroundTest, runBackgroundCase, runBackgroundFlashcards,
      runBackgroundGamma, runBackgroundResumen, registerReady 
    }),
    [generations, start, markReady, markError, markSeen, clear, runBackgroundTest, runBackgroundCase, runBackgroundFlashcards, runBackgroundGamma, runBackgroundResumen, registerReady],
  );

  return <PendingGenContext.Provider value={value}>{children}</PendingGenContext.Provider>;
}
